/**
 * Photo URL helpers — resolve product photos to pre-generated thumbnail sizes
 * (see backend/export_quotes.py::THUMBNAIL_SIZES).
 *
 * Any product photo uploaded via /api/products/upload-image or attached via
 * PUT /api/products/{id} triggers a fire-and-forget task on the backend that
 * generates the 4 named sizes below. Callers pick the size that best matches
 * their layout to avoid pulling multi-MB originals into mobile lists.
 *
 * URL shape:   /api/uploads/<uuid>.jpg   →   /api/uploads/thumb_md_<uuid>.png
 *
 * 2026-07 · Direct-R2 fast path: after the app boots we prime
 * ``window.__quotesys_r2_public_url__`` (see AuthContext) with the Cloudflare
 * R2 public URL. When set, ``photoThumb`` rewrites the path to a direct R2
 * link — bypassing our K8s ingress entirely so the browser gets full
 * long-lived caching and one less RTT per image.
 *
 * If the thumbnail doesn't exist yet (legacy photos uploaded before the warmer
 * shipped, or the background task failed) the backend falls through to on-demand
 * generation via `email_photo_url` / `thumbnail_url` on first hit.
 */
const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const PREFIX_BY_SIZE = {
  mail: "mail",         // 240×180 · email attachment
  sm: "thumb_sm",       // 120×120 · mobile list / chip / label
  md: "thumb_md",       // 360×360 · product cards / quote grid / /scan
  lg: "thumb_lg",       // 800×800 · lightbox / detail
};

/** Return the R2 public URL prefix if configured at runtime. */
function r2Prefix() {
  return (typeof window !== "undefined" && window.__quotesys_r2_public_url__) || "";
}

/** 2026-10-11 · Market-survey bucket public URL (separate from the primary
 *  bucket after dual-bucket split). Empty string when the user hasn't yet
 *  enabled public access on the ``marketphoto`` bucket — in that case we
 *  deliberately return "" so callers fall back to the backend proxy
 *  (``/api/uploads/*``) which reads from the correct bucket. */
function r2MarketPrefix() {
  return (typeof window !== "undefined" && window.__quotesys_r2_market_public_url__) || "";
}

/** True when the given filename lives on the market-survey bucket.
 *  Mirrors backend/storage.py::_is_survey_key. */
function isSurveyKey(stem, ext) {
  const name = `${stem}.${ext || ""}`;
  return name.startsWith("survey-") || name.startsWith("survey-fm-");
}

/**
 * Return an absolute URL for the requested thumbnail size. Falls back to the
 * original photo URL when we can't confidently rewrite the path (external URL,
 * malformed, etc.) so nothing ever renders broken.
 *
 * @param {string} photoUrl - relative `/api/uploads/xxx.ext` or absolute URL
 * @param {"sm"|"md"|"lg"|"mail"} size
 * @returns {string} absolute URL
 */
export function photoThumb(photoUrl, size = "md") {
  if (!photoUrl) return "";
  const prefix = PREFIX_BY_SIZE[size];
  if (!prefix) return absolutise(photoUrl);
  // Only rewrite our own /api/uploads/<filename>.<ext>
  const m = photoUrl.match(/^\/api\/uploads\/([^/]+)\.([^./]+)$/);
  if (!m) return absolutise(photoUrl);
  const stem = m[1];
  const ext = m[2];
  // 2026-10-11 · Survey keys live on the market bucket. Use its CDN if
  // configured; otherwise fall through to the backend proxy so we don't
  // 404 on the primary bucket.
  if (isSurveyKey(stem, ext)) {
    const marketR2 = r2MarketPrefix();
    if (marketR2) return `${marketR2}/${prefix}_${stem}.png`;
    return `${BACKEND}/api/uploads/${prefix}_${stem}.png`;
  }
  const r2 = r2Prefix();
  if (r2) return `${r2}/${prefix}_${stem}.png`;
  return `${BACKEND}/api/uploads/${prefix}_${stem}.png`;
}

/**
 * Backend URL for a thumbnail — used as an ``onError`` fallback so images that
 * never had their thumbnail warmed to R2 still resolve. Hitting the backend
 * triggers on-demand generation (see ``serve_upload`` in server.py) and pushes
 * the newly-rendered PNG to R2, so the next page load hits the CDN directly.
 */
export function photoThumbBackend(photoUrl, size = "md") {
  if (!photoUrl) return "";
  const prefix = PREFIX_BY_SIZE[size];
  if (!prefix) return absolutise(photoUrl);
  const m = photoUrl.match(/^\/api\/uploads\/([^/]+)\.[^./]+$/);
  if (!m) return absolutise(photoUrl);
  return `${BACKEND}/api/uploads/${prefix}_${m[1]}.png`;
}

/** Absolute URL for the ORIGINAL (full-resolution) photo — use sparingly. */
export function photoFull(photoUrl) {
  if (!photoUrl) return "";
  const m = photoUrl.match(/^\/api\/uploads\/([^/]+)\.([^./]+)$/);
  if (m) {
    const stem = m[1];
    const ext = m[2];
    // 2026-10-11 · Route survey keys to the market bucket CDN; empty →
    // backend proxy so uploads display immediately on preview too.
    if (isSurveyKey(stem, ext)) {
      const marketR2 = r2MarketPrefix();
      if (marketR2) return `${marketR2}/${stem}.${ext}`;
      return `${BACKEND}/api/uploads/${stem}.${ext}`;
    }
    const r2 = r2Prefix();
    if (r2) return `${r2}/${stem}.${ext}`;
  }
  return absolutise(photoUrl);
}

function absolutise(u) {
  if (!u) return "";
  if (/^https?:/i.test(u)) return u;
  return `${BACKEND}${u.startsWith("/") ? "" : "/"}${u}`;
}
